import { useState, useEffect, useRef } from 'react';
import { 
  Copy, 
  Clipboard,
  Trash2, 
  ArrowUpToLine, 
  ArrowDownToLine,
  Group,
  Ungroup,
  Lock,
  Unlock,
  Palette,
  PenLine,
  Type,
  FlipHorizontal,
  FlipVertical,
  RotateCw,
  ChevronRight
} from 'lucide-react';

// Color presets
const colorPresets = [
  { color: '#1E293B', name: 'Dark' },
  { color: '#64748B', name: 'Gray' },
  { color: '#EF4444', name: 'Red' },
  { color: '#F97316', name: 'Orange' },
  { color: '#EAB308', name: 'Yellow' },
  { color: '#22C55E', name: 'Green' },
  { color: '#3B82F6', name: 'Blue' },
  { color: '#6366F1', name: 'Indigo' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#FFFFFF', name: 'White' },
  { color: 'transparent', name: 'None' },
];

export const WhiteboardContextMenu = ({
  x,
  y,
  objectIds,
  objects = [],
  onClose,
  onDelete,
  onDuplicate,
  onCopy,
  onPaste,
  onBringToFront,
  onSendToBack,
  onGroup,
  onUngroup,
  onLock,
  onUnlock,
  onFlipHorizontal,
  onFlipVertical,
  onRotate,
  onChangeFillColor,
  onChangeStrokeColor,
  onChangeTextColor,
  clipboardHasContent = false,
}) => {
  const menuRef = useRef(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Check properties of selected objects
  const selectedObjs = objects.filter(obj => objectIds.includes(obj.id));
  const hasGroupedObjects = selectedObjs.some(obj => obj.groupId);
  const hasLockedObjects = selectedObjs.some(obj => obj.locked);
  const hasUnlockedObjects = selectedObjs.some(obj => !obj.locked);
  const hasShapes = selectedObjs.some(obj => obj.type === 'shape');
  const hasStickies = selectedObjs.some(obj => obj.type === 'sticky');
  const hasText = selectedObjs.some(obj => obj.type === 'text');
  const canChangeColor = hasShapes || hasStickies || hasText;

  // Get current colors from first selected object
  const firstObj = selectedObjs[0];
  const currentFillColor = firstObj?.fill || firstObj?.color || '#FFFFFF';
  const currentStrokeColor = firstObj?.stroke || '#1E293B';
  const currentTextColor = firstObj?.textColor || firstObj?.color || '#1E293B';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Adjust menu position if it goes off screen
  const adjustedX = Math.min(x, window.innerWidth - 220);
  const adjustedY = Math.min(y, window.innerHeight - 400);

  const handleColorChange = (type, color) => {
    if (type === 'fill' && onChangeFillColor) {
      onChangeFillColor(color);
    } else if (type === 'stroke' && onChangeStrokeColor) {
      onChangeStrokeColor(color);
    } else if (type === 'text' && onChangeTextColor) {
      onChangeTextColor(color);
    }
    setActiveSubmenu(null);
    onClose();
  };

  const renderColorSubmenu = (type, currentColor) => (
    <div 
      className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 min-w-[180px]"
      style={{ marginTop: '-8px' }}
    >
      <div className="grid grid-cols-6 gap-1">
        {colorPresets.map((c) => (
          <button
            key={c.color}
            className={`w-6 h-6 rounded border-2 transition-all hover:scale-110 ${
              currentColor === c.color 
                ? 'border-blue-500' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={{ 
              backgroundColor: c.color === 'transparent' ? 'white' : c.color,
              backgroundImage: c.color === 'transparent' 
                ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                : 'none',
              backgroundSize: '6px 6px',
              backgroundPosition: '0 0, 3px 3px'
            }}
            onClick={() => handleColorChange(type, c.color)}
            title={c.name}
          />
        ))}
      </div>
    </div>
  );

  const MenuItem = ({ icon: Icon, label, shortcut, onClick, danger, disabled, hasSubmenu, submenuType }) => (
    <div className="relative">
      <button
        onClick={hasSubmenu ? () => setActiveSubmenu(activeSubmenu === submenuType ? null : submenuType) : onClick}
        disabled={disabled}
        className={`w-full px-3 py-2 flex items-center justify-between text-sm transition-colors ${
          disabled 
            ? 'text-gray-300 cursor-not-allowed' 
            : danger 
              ? 'text-red-600 hover:bg-red-50' 
              : 'text-[#1E293B] hover:bg-gray-50'
        } ${activeSubmenu === submenuType ? 'bg-gray-50' : ''}`}
      >
        <div className="flex items-center gap-2">
          <Icon size={16} />
          {label}
        </div>
        {shortcut && !hasSubmenu && (
          <span className="text-xs text-[#94A3B8]">{shortcut}</span>
        )}
        {hasSubmenu && <ChevronRight size={14} className="text-gray-400" />}
      </button>
      
      {hasSubmenu && activeSubmenu === submenuType && (
        submenuType === 'fill' ? renderColorSubmenu('fill', currentFillColor) :
        submenuType === 'stroke' ? renderColorSubmenu('stroke', currentStrokeColor) :
        submenuType === 'text' ? renderColorSubmenu('text', currentTextColor) : null
      )}
    </div>
  );

  const Divider = () => <div className="h-px bg-gray-100 my-1" />;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px] z-[60]"
      style={{ left: adjustedX, top: adjustedY }}
      data-testid="context-menu"
    >
      {objectIds.length > 0 ? (
        <>
          {/* Edit Actions */}
          <MenuItem icon={Copy} label="Duplicate" shortcut="Ctrl+D" onClick={onDuplicate} />
          {onCopy && <MenuItem icon={Copy} label="Copy" shortcut="Ctrl+C" onClick={onCopy} />}
          {onPaste && clipboardHasContent && <MenuItem icon={Clipboard} label="Paste" shortcut="Ctrl+V" onClick={onPaste} />}
          <MenuItem icon={Trash2} label="Delete" shortcut="Del" onClick={onDelete} danger />
          
          <Divider />
          
          {/* Layer Actions */}
          <MenuItem icon={ArrowUpToLine} label="Bring to front" onClick={onBringToFront} />
          <MenuItem icon={ArrowDownToLine} label="Send to back" onClick={onSendToBack} />
          
          {/* Color Actions - only show if objects can have colors */}
          {canChangeColor && (
            <>
              <Divider />
              {(hasShapes || hasStickies) && (
                <MenuItem 
                  icon={Palette} 
                  label="Fill color" 
                  hasSubmenu 
                  submenuType="fill"
                />
              )}
              {hasShapes && (
                <MenuItem 
                  icon={PenLine} 
                  label="Stroke color" 
                  hasSubmenu 
                  submenuType="stroke"
                />
              )}
              {(hasShapes || hasStickies || hasText) && (
                <MenuItem 
                  icon={Type} 
                  label="Text color" 
                  hasSubmenu 
                  submenuType="text"
                />
              )}
            </>
          )}
          
          {/* Transform Actions */}
          {(onFlipHorizontal || onFlipVertical || onRotate) && (
            <>
              <Divider />
              {onFlipHorizontal && <MenuItem icon={FlipHorizontal} label="Flip horizontal" onClick={onFlipHorizontal} />}
              {onFlipVertical && <MenuItem icon={FlipVertical} label="Flip vertical" onClick={onFlipVertical} />}
              {onRotate && <MenuItem icon={RotateCw} label="Rotate 90°" onClick={onRotate} />}
            </>
          )}
          
          {/* Group Actions */}
          {(objectIds.length > 1 || hasGroupedObjects) && (
            <>
              <Divider />
              {objectIds.length > 1 && <MenuItem icon={Group} label="Group" shortcut="Ctrl+G" onClick={onGroup} />}
              {hasGroupedObjects && <MenuItem icon={Ungroup} label="Ungroup" shortcut="Ctrl+⇧+G" onClick={onUngroup} />}
            </>
          )}
          
          {/* Lock Actions */}
          {(onLock || onUnlock) && (
            <>
              <Divider />
              {hasUnlockedObjects && onLock && <MenuItem icon={Lock} label="Lock" onClick={onLock} />}
              {hasLockedObjects && onUnlock && <MenuItem icon={Unlock} label="Unlock" onClick={onUnlock} />}
            </>
          )}
        </>
      ) : (
        <>
          {/* Canvas right-click (no selection) */}
          {onPaste && clipboardHasContent && (
            <MenuItem icon={Clipboard} label="Paste" shortcut="Ctrl+V" onClick={onPaste} />
          )}
          {(!onPaste || !clipboardHasContent) && (
            <div className="px-3 py-2 text-sm text-[#94A3B8]">
              No actions available
            </div>
          )}
        </>
      )}
    </div>
  );
};
